module Client.Page.User.MyTipburnStations

open Client
open Client.DomainTypes
open Client.Forms
open Client.Infrastructure.Api
open Client.Components
open Client.Domain
open Client.InfrastructureTypes
open Client.DomainTypes.Msg
open Elmish
open Fable.FontAwesome
open Fable.React
open Fable.React.Props
open Fulma
open Shared
open Shared.Dto.Dto
open Shared.Dto.Page
open Thoth.Elmish

type DataModel = {
    Session: UserSession
    Stations: TipBurn.StationListingDto list
    DeleteModal: TipBurn.StationListingDto option
    RenameModal: RenameTipBurnStation.Model option
    DeletionRunning: bool
}

type Model = Loadable<DataModel, UserSession>

let init (session: UserSession) : Model * Cmd<Msg> =
    let request = {
        SessionKey = session.SessionKey
        Data = ()
    }

    Loadable.Loading session,
    Cmd.OfAsync.either
        api.getMyTipBurnStations
        request
        (MyTipBurnStationsMsg.StationsReceived >> MyTipBurnStations)
        (MyTipBurnStationsMsg.LoadingStationsFailed >> MyTipBurnStations)

let private stationsResponseReceived loadingModel (response: AuthenticatedResponse<TipBurn.StationListingDto list>) =
    match response with
    | Ok stations ->
        Loadable.Data {
            Session = loadingModel
            Stations = stations
            DeleteModal = None
            RenameModal = None
            DeletionRunning = false
        },
        Cmd.none
    | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none

let update (msg: MyTipBurnStationsMsg) (model: Model) : Model * Cmd<Msg> =
    match msg, model with
    | MyTipBurnStationsMsg.StationsReceived response, Loadable.Loading loadingModel ->
        stationsResponseReceived loadingModel response
    | MyTipBurnStationsMsg.LoadingStationsFailed _, _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | MyTipBurnStationsMsg.OpenDeleteModal station, Loadable.Data data ->
        Loadable.Data {
            data with
                DeleteModal = Some station
                DeletionRunning = false
        },
        Cmd.none
    | MyTipBurnStationsMsg.CloseDeleteModal, Loadable.Data data ->
        Loadable.Data {
            data with
                DeleteModal = None
                DeletionRunning = false
        },
        Cmd.none
    | MyTipBurnStationsMsg.DeleteStation id, Loadable.Data data ->
        let requestData: AuthenticatedRequest<int> = {
            SessionKey = data.Session.SessionKey
            Data = id
        }

        let cmd =
            Cmd.OfAsync.perform
                api.deleteTipBurnStation
                requestData
                (MyTipBurnStationsMsg.StationDeleted >> MyTipBurnStations)

        Loadable.Data { data with DeletionRunning = true }, cmd
    | MyTipBurnStationsMsg.StationDeleted response, Loadable.Data data ->
        match response with
        | Ok(TipBurn.DeletionResponse.Successful id) ->
            let toastCmd =
                Toast.create "Die Tipburn-Station wurde erfolgreich gelöscht" |> Toast.success

            Loadable.Data {
                data with
                    Stations = List.filter (fun station -> station.Id <> id) data.Stations
                    DeleteModal = None
            },
            toastCmd
        | Ok TipBurn.DeletionResponse.NotFound ->
            let toastCmd =
                Toast.create "Die Tipburn-Station wurde nicht gefunden" |> Toast.error

            Loadable.Data { data with DeleteModal = None }, toastCmd
        | AuthenticatedResponse.Error _ ->
            let toastCmd =
                Toast.create "Beim Löschen der Tipburn-Station ist ein Fehler aufgetreten"
                |> Toast.error

            Loadable.Data data, toastCmd
    | MyTipBurnStationsMsg.OpenRenameModal station, Loadable.Data dataModel ->
        Loadable.Data { dataModel with RenameModal = Some(RenameTipBurnStation.init station) }, Cmd.none
    | MyTipBurnStationsMsg.RenameModal subMsg, Loadable.Data dataModel ->
        match dataModel.RenameModal with
        | Some modal ->
            let newModel, result = RenameTipBurnStation.update subMsg modal

            match result with
            | RenameTipBurnStation.Noop -> Loadable.Data { dataModel with RenameModal = Some newModel }, Cmd.none
            | RenameTipBurnStation.CloseModal -> Loadable.Data { dataModel with RenameModal = None }, Cmd.none
            | RenameTipBurnStation.Rename requestData ->
                let request = {
                    SessionKey = dataModel.Session.SessionKey
                    Data = requestData
                }

                let cmd =
                    Cmd.OfAsync.perform
                        api.renameTipBurnStation
                        request
                        (MyTipBurnStationsMsg.StationRenamed >> MyTipBurnStations)

                Loadable.Data { dataModel with RenameModal = Some newModel }, cmd
        | None -> Loadable.Data dataModel, Cmd.none
    | MyTipBurnStationsMsg.StationRenamed response, Loadable.Data dataModel ->
        match response with
        | Ok _ ->
            let toastCmd =
                Toast.create "Die TipBurn-Station wurde erfolgreich umbenannt" |> Toast.success

            init dataModel.Session |> fun (model, cmd) -> model, Cmd.batch [ toastCmd; cmd ]
        | AuthenticatedResponse.Error err ->
            let cmd = ErrorConfirmation.responseError ErrorConfirmation.defaultConfig err

            Loadable.Data { dataModel with RenameModal = None }, cmd
    | _, _ -> model, Cmd.none

let private createDeleteModalConfig dispatch (station: TipBurn.StationListingDto) : ConfirmationModal.Configuration = {
    Headline = "Tipburn-Station löschen"
    Text = sprintf "Willst du wirklich die Tipburn-Station '%s' löschen?" station.Name
    OnClose = (fun _ -> dispatch MyTipBurnStationsMsg.CloseDeleteModal)
    OnNo = (fun _ -> dispatch MyTipBurnStationsMsg.CloseDeleteModal)
    OnYes = (fun _ -> dispatch (MyTipBurnStationsMsg.DeleteStation station.Id))
}

let private addButton =
    let button =
        Button.a [
            Button.Color IsLink
            Button.Props [
                Href "https://mysens.eu/order_tip/"
                Target "_blank"
            ]
        ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.Plus ] [] ]
            span [] [ str "Hinzufügen" ]
        ]

    Level.level [] [
        Level.left [] []
        Level.right [] [ Level.item [] [ button ] ]
    ]

let private deleteButton dispatch station =
    Button.button [
        Button.Color IsDanger
        CommonButtons.onClick (fun _ ->
            station |> MyTipBurnStationsMsg.OpenDeleteModal |> MyTipBurnStations |> dispatch
        )
    ] [
        Icon.icon [] [ Fa.i [ Fa.Solid.Trash ] [] ]
        span [] [ str "Löschen" ]
    ]

let private renameButton dispatch station =
    Button.button [
        Button.Color Color.IsLink
        CommonButtons.onClick (fun _ ->
            station |> MyTipBurnStationsMsg.OpenRenameModal |> MyTipBurnStations |> dispatch
        )
    ] [
        Icon.icon [] [ Fa.i [ Fa.Solid.Pen ] [] ]
        span [] [ str "Umbenennen" ]
    ]


let private stationRow dispatch (station: TipBurn.StationListingDto) =
    let sensors =
        [ station.AirSensorName; station.SoilSensorName ]
        |> List.distinct
        |> List.sort
        |> String.concat ", "

    let currentRisk =
        match station.CurrentRisk with
        | Some risk -> string risk
        | None -> "-"

    let dataRoute = Route.TipBurnData station.Id

    Columns.columns [ Columns.IsVCentered; Columns.bottomBorder ] [
        Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [
            a [
                ClassName "entity-link"
                Clickable.onClick (fun _ -> dispatch (GlobalMsg.GoToRoute dataRoute |> Global))
                Href(Routing.routeToUrl dataRoute)
            ] [ str station.Name ]
        ]
        Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [
            span [ Html.label ] [ str "Aktuelles Risiko:" ]
            span [ Style [ FontWeight "bold" ] ] [ str currentRisk ]
        ]
        Column.column [] [
            span [ Html.label ] [ str "Sensoren:" ]
            span [ Style [ FontWeight "bold" ] ] [ str sensors ]
        ]
        Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [ renameButton dispatch station ]
        Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [ deleteButton dispatch station ]
    ]

let rowsView dispatch (stations: TipBurn.StationListingDto list) =
    if List.isEmpty stations then
        [
            p [] [ str "Keine TipBurn Stationen gefunden" ]
        ]
    else
        List.map (stationRow dispatch) stations

let dataView dispatch (model: DataModel) =
    Container.container [ Container.isFullWidth ] [
        Box.box' [ GenericOption.CustomClass "grey_box" ] [
            Heading.p [] [ str "TipBurn Stationen" ]

            yield! (rowsView dispatch model.Stations)

            addButton
        ]

        match model.DeleteModal with
        | Some station ->
            createDeleteModalConfig (MyTipBurnStations >> dispatch) station
            |> ConfirmationModal.view model.DeletionRunning
        | None -> ()

        match model.RenameModal with
        | Some modal ->
            RenameTipBurnStation.view (MyTipBurnStationsMsg.RenameModal >> MyTipBurnStations >> dispatch) modal
        | None -> ()
    ]

let view dispatch (model: Model) = Loadable.view (dataView dispatch) model